import { type FormEvent, useState } from 'react';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
// import { BsTelegram } from 'react-icons/bs'; // Telegram MFA hidden for now
import { SiGoogleauthenticator } from 'react-icons/si';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  type MfaMethod,
  // useConfirmTelegramLinkMutation, // Telegram MFA hidden for now
  useConfirmMfaSetupMutation,
  // useGetMfaStatusQuery, // Telegram MFA hidden for now
  // useSendTelegramDisableOtpMutation, // Telegram MFA hidden for now
  useSetupMfaMutation,
  // useStartTelegramLinkMutation, // Telegram MFA hidden for now
  // useUnlinkTelegramMfaMutation, // Telegram MFA hidden for now
} from '@/store/api/authApi';
import { showToastMessage } from '@/utils';

type Props = {
  open: boolean;
  action: 'enable' | 'disable';
  activeMethod: MfaMethod | null;
  defaultUsername?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfigured: () => Promise<void> | void;
  onDisabled: () => void;
};

const getError = (error: unknown) => {
  const value = error as {
    data?: { message?: string | string[] };
    error?: string;
    message?: string;
  };
  const message =
    value?.data?.message ?? value?.error ?? value?.message ?? 'Unable to update MFA settings.';
  return Array.isArray(message) ? message.join(', ') : message;
};

type PasswordInputProps = {
  value: string;
  onChange: (value: string) => void;
};

function PasswordInput({ value, onChange }: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const Icon = showPassword ? EyeOff : Eye;

  return (
    <div className="relative">
      <Input
        aria-label="Password"
        autoComplete="current-password"
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Password"
        className="border border-zinc-400 pr-10"
      />
      <button
        type="button"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
        onClick={() => setShowPassword((current) => !current)}
      >
        <Icon className="h-4 w-4" />
      </button>
    </div>
  );
}

export function MfaSettingsModal({
  open,
  action,
  activeMethod,
  defaultUsername,
  onOpenChange,
  onConfigured,
  onDisabled,
}: Props) {
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [token, setToken] = useState('');
  const [usernameOrEmail, setUsernameOrEmail] = useState(defaultUsername ?? '');
  const [password, setPassword] = useState('');
  // Telegram MFA hidden for now
  // const [telegramLink, setTelegramLink] = useState<{
  //   linkUrl: string;
  //   code: string;
  //   expiresInSeconds: number;
  // } | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setupMfa, { isLoading: isSettingUp }] = useSetupMfaMutation();
  const [confirmMfaSetup, { isLoading: isConfirmingSetup }] = useConfirmMfaSetupMutation();
  // Telegram MFA hidden for now
  // const [startTelegramLink, { isLoading: isStartingTelegramLink }] = useStartTelegramLinkMutation();
  // const [confirmTelegramLink, { isLoading: isConfirmingTelegramLink }] =
  //   useConfirmTelegramLinkMutation();
  // const [sendTelegramDisableOtp, { isLoading: isSendingDisableOtp }] =
  //   useSendTelegramDisableOtpMutation();
  // const [unlinkTelegramMfa, { isLoading: isUnlinkingTelegram }] = useUnlinkTelegramMfaMutation();
  // const { data: mfaStatus } = useGetMfaStatusQuery(undefined, {
  //   skip: !open || action !== 'enable' || method !== 'TELEGRAM' || !telegramLink,
  //   pollingInterval: 3000,
  // });

  const busy = isSettingUp || isConfirmingSetup;
  // Telegram MFA hidden for now
  // const busy =
  //   isSettingUp ||
  //   isConfirmingSetup ||
  //   isStartingTelegramLink ||
  //   isConfirmingTelegramLink ||
  //   isSendingDisableOtp ||
  //   isUnlinkingTelegram;
  // const telegramPending =
  //   typeof mfaStatus?.telegram === 'object' ? Boolean(mfaStatus.telegram.pending) : false;

  const resetLocalState = () => {
    setMethod(null);
    setToken('');
    setUsernameOrEmail(defaultUsername ?? '');
    setPassword('');
    // setTelegramLink(null); // Telegram MFA hidden for now
    setQrCode(null);
    setSecret(null);
    setError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && busy) return;
    if (!nextOpen) resetLocalState();
    onOpenChange(nextOpen);
  };

  const startGoogleSetup = () => {
    setError(null);
    setMethod('GOOGLE');
  };

  const submitGoogleSetupCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!usernameOrEmail.trim() || !password) {
      setError('Enter username and password to continue MFA setup.');
      return;
    }
    if (qrCode) return;
    try {
      const result = await setupMfa({
        usernameOrEmail: usernameOrEmail.trim(),
        password,
        status: 'ACTIVE',
      }).unwrap();
      if (!('qrCode' in result)) throw new Error('MFA setup did not return a QR code.');
      setQrCode(result.qrCode);
      setSecret(result.secret);
    } catch (reason) {
      setError(getError(reason));
    }
  };

  const confirmGoogleSetup = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(token)) {
      setError('Enter the six-digit code from Google Authenticator.');
      return;
    }
    try {
      await confirmMfaSetup({ token }).unwrap();
      showToastMessage('success', 'MFA enabled successfully. Please log in again.');
      await onConfigured();
    } catch (reason) {
      setError(getError(reason));
    }
  };

  // Telegram MFA hidden for now
  // const startTelegramSetup = async () => {
  //   try {
  //     setError(null);
  //     setMethod('TELEGRAM');
  //     setTelegramLink(await startTelegramLink().unwrap());
  //   } catch (reason) {
  //     setError(getError(reason));
  //   }
  // };
//
  // const confirmTelegramSetup = async (event: FormEvent) => {
  //   event.preventDefault();
  //   setError(null);
  //   if (!/^\d{6}$/.test(token)) {
  //     setError('Enter the six-digit code sent to Telegram.');
  //     return;
  //   }
  //   try {
  //     await confirmTelegramLink({ token }).unwrap();
  //     showToastMessage('success', 'MFA enabled successfully. Please log in again.');
  //     await onConfigured();
  //   } catch (reason) {
  //     setError(getError(reason));
  //   }
  // };

  const disableGoogle = async (event: FormEvent) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(token)) {
      setError('Enter the six-digit code from Google Authenticator.');
      return;
    }
    try {
      await setupMfa({
        status: 'INACTIVE',
        token,
      }).unwrap();
      showToastMessage('success', 'MFA disabled successfully.');
      resetLocalState();
      onDisabled();
    } catch (reason) {
      setError(getError(reason));
    }
  };

  // Telegram MFA hidden for now
  // const sendDisableTelegramOtp = async () => {
  //   try {
  //     setError(null);
  //     const result = await sendTelegramDisableOtp().unwrap();
  //     showToastMessage(
  //       'success',
  //       `Telegram code sent. It expires in ${Math.floor(result.expiresInSeconds / 60)} minutes.`
  //     );
  //   } catch (reason) {
  //     setError(getError(reason));
  //   }
  // };
//
  // const disableTelegram = async (event: FormEvent) => {
  //   event.preventDefault();
  //   setError(null);
  //   if (!/^\d{6}$/.test(token)) {
  //     setError('Enter the six-digit code sent to Telegram.');
  //     return;
  //   }
  //   try {
  //     await unlinkTelegramMfa({ token }).unwrap();
  //     showToastMessage('success', 'MFA disabled successfully.');
  //     resetLocalState();
  //     onDisabled();
  //   } catch (reason) {
  //     setError(getError(reason));
  //   }
  // };

  const title = action === 'enable' ? 'Set up MFA' : 'Disable MFA';
  const description =
    action === 'enable'
      ? 'Choose an authentication method and complete verification.'
      : 'Verify your current MFA method to turn it off.';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-white text-foreground">
        <DialogHeader>
          <ShieldCheck className="mx-auto h-9 w-9 text-primary" />
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center">{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {action === 'enable' && !method && (
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-24 flex-col gap-2"
              onClick={startGoogleSetup}
              disabled={busy}
            >
              <SiGoogleauthenticator className="size-6" /> Google Authenticator
            </Button>
            {/* Telegram MFA hidden for now
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-24 flex-col gap-2"
              onClick={startTelegramSetup}
              disabled={busy}
            >
              <BsTelegram className="size-7" /> Telegram
            </Button>
            */}
          </div>
        )}

        {action === 'enable' && method === 'GOOGLE' && (
          <form
            onSubmit={qrCode ? confirmGoogleSetup : submitGoogleSetupCredentials}
            className="space-y-4 text-center"
          >
            {isSettingUp && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
            {!qrCode && (
              <>
                <Input
                  aria-label="Username or email"
                  autoComplete="username"
                  value={usernameOrEmail}
                  onChange={(event) => setUsernameOrEmail(event.target.value)}
                  placeholder="Username or email"
                  className="border border-zinc-400"
                />
                <PasswordInput value={password} onChange={setPassword} />
                <Button type="submit" className="w-full" disabled={busy}>
                  {isSettingUp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Continue
                </Button>
              </>
            )}
            {qrCode && (
              <img src={qrCode} className="mx-auto h-48 w-48" alt="Google MFA setup QR code" />
            )}
            {secret && (
              <p className="break-all text-xs text-muted-foreground">
                Manual key: <code>{secret}</code>
              </p>
            )}
            {qrCode && (
              <>
                <Input
                  aria-label="Authenticator code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={token}
                  onChange={(event) => setToken(event.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit authentication code"
                  className="border border-zinc-400"
                />
                <Button type="submit" className="w-full" disabled={busy}>
                  {isConfirmingSetup && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enable MFA
                </Button>
              </>
            )}
          </form>
        )}

        {/* Telegram MFA hidden for now
        {action === 'enable' && method === 'TELEGRAM' && (
          <form onSubmit={confirmTelegramSetup} className="space-y-4">
            {isStartingTelegramLink && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
            {telegramLink && (
              <>
                <Button type="button" variant="outline" className="w-full" asChild>
                  <a href={telegramLink.linkUrl} target="_blank" rel="noreferrer">
                    Open Telegram bot
                  </a>
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  Linking code {telegramLink.code} expires in{' '}
                  {Math.floor(telegramLink.expiresInSeconds / 60)} minutes. After Telegram sends
                  your confirmation code, enter it below.
                </p>
              </>
            )}
            <Input
              aria-label="Telegram confirmation code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={token}
              onChange={(event) => setToken(event.target.value.replace(/\D/g, ''))}
              placeholder={telegramPending ? '6-digit Telegram code' : 'Waiting for Telegram code'}
              className="border border-zinc-400"
            />
            <Button type="submit" className="w-full" disabled={busy || !telegramLink}>
              {isConfirmingTelegramLink && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enable MFA
            </Button>
          </form>
        )}
        */}
        {action === 'disable' && activeMethod === 'GOOGLE' && (
          <form onSubmit={disableGoogle} className="space-y-4">
            <Input
              aria-label="Authenticator code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={token}
              onChange={(event) => setToken(event.target.value.replace(/\D/g, ''))}
              placeholder="6-digit authentication code"
              className="border border-zinc-400"
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {isSettingUp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Disable MFA
            </Button>
          </form>
        )}

        {/* Telegram MFA hidden for now
        {action === 'disable' && activeMethod === 'TELEGRAM' && (
          <form onSubmit={disableTelegram} className="space-y-4">
            <p className="text-center text-xs text-muted-foreground">
              Send a one-time code to your linked Telegram account to disable MFA.
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={sendDisableTelegramOtp}
              disabled={busy}
            >
              {isSendingDisableOtp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send Telegram code
            </Button>
            <Input
              aria-label="Telegram code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={token}
              onChange={(event) => setToken(event.target.value.replace(/\D/g, ''))}
              placeholder="6-digit Telegram code"
              className="border border-zinc-400"
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {isUnlinkingTelegram && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Disable MFA
            </Button>
          </form>
        )}
        */}
      </DialogContent>
    </Dialog>
  );
}
